import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DynamicFieldDialog from "@/components/settings/DynamicFieldDialog";

const TYPE_LABELS = {
  text: "Text", textarea: "Text area", number: "Number", date: "Date",
  dropdown: "Dropdown", multiselect: "Multi-select", checkbox: "Checkbox", phone: "Phone",
};
const DIM_LABELS = {
  main_category: "Main Category", sub_category: "Sub Category", issue_type: "Issue Type",
  department: "Department", channel: "Channel", ticket_type: "Ticket Type",
};

function triggerSummary(f) {
  const rules = f.conditions?.rules || [];
  if (rules.length === 0) return "Always";
  const join = (f.conditions?.match === "any") ? " OR " : " AND ";
  return rules.map((r) => `${DIM_LABELS[r.dimension] || r.dimension} (${(r.values || []).length})`).join(join);
}

export default function DynamicFieldsManager() {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("dynamic_fields.create");
  const canEdit = hasPerm("dynamic_fields.edit");
  const canDelete = hasPerm("dynamic_fields.delete");
  const [fields, setFields] = useState(null);
  const [dialogField, setDialogField] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => api.get("/dynamic-fields").then((r) => setFields(r.data)).catch(() => setFields([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setDialogField(null); setDialogOpen(true); };
  const openEdit = (f) => { setDialogField(f); setDialogOpen(true); };
  const remove = async (f) => {
    if (!window.confirm(`Delete field "${f.label}"? Existing tickets keep their stored value.`)) return;
    try { await api.delete(`/dynamic-fields/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="border-slate-200" data-testid="dynamic-fields-manager">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-sm font-semibold text-slate-800">Conditional Fields</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Extra ticket fields that appear based on category, department, channel or ticket type — fully configurable, no code needed.
            </p>
          </div>
          {canCreate && (
            <Button size="sm" onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-dynamic-field">
              <Plus className="w-3.5 h-3.5 mr-1.5" />New field
            </Button>
          )}
        </div>

        {fields === null ? (
          <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : fields.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-400">
            No conditional fields yet.{canCreate && " Click “New field” to add one."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Field</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Shows when</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Status</TableHead>
                {(canEdit || canDelete) && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((f) => (
                <TableRow key={f.id} data-testid={`df-row-${f.key}`}>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-700">{f.label}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{f.key}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{TYPE_LABELS[f.field_type] || f.field_type}</TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-xs truncate">{triggerSummary(f)}</TableCell>
                  <TableCell>{f.required ? <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Mandatory</Badge> : <span className="text-xs text-slate-400">Optional</span>}</TableCell>
                  <TableCell>{f.active ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge> : <span className="text-xs text-slate-400">Off</span>}</TableCell>
                  {(canEdit || canDelete) && (
                    <TableCell className="text-right">
                      {canEdit && <Button variant="ghost" size="sm" onClick={() => openEdit(f)} data-testid={`edit-df-${f.key}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                      {canDelete && <Button variant="ghost" size="sm" onClick={() => remove(f)} className="text-rose-600" data-testid={`delete-df-${f.key}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <DynamicFieldDialog open={dialogOpen} onOpenChange={setDialogOpen} field={dialogField} onSaved={load} />
    </Card>
  );
}
